import { supabase } from './supabase';
import type { Folder } from '@/hooks/useFolders';

// The "Myspace Tom" guest/demo curator. Username "Myspace Tom" maps to this
// synthetic email; password is curated in the DB migration. Logging in as this
// user lets you curate the starter files & folders everyone begins with.
export const DEFAULT_USER_EMAIL = 'default@telekinesis.app';
export const DEFAULT_USERNAME = 'Myspace Tom';

// localStorage keys — the folder keys mirror useFolders.ts.
const FOLDERS_KEY = 'tk_folders';
const ASSIGNMENTS_KEY = 'tk_file_folders';
const CLAIMED_KEY = 'tk_account_claimed';
const SEEDED_KEY = 'tk_default_seeded';

export interface DefaultWorkspace {
  defaultUserId: string;
  folders: Folder[];
  fileFolders: Record<string, string>;
}

export const DEFAULT_WORKSPACE_QUERY_KEY = ['default-workspace'] as const;
export const DEFAULT_WORKSPACE_STALE_TIME = 5 * 60_000;

/** Map a login identifier to an email. "Myspace Tom" (no @) → the synthetic email. */
export function resolveLoginIdentifier(identifier: string): string {
  const trimmed = identifier.trim();
  if (
    !trimmed.includes('@') &&
    trimmed.toLowerCase() === DEFAULT_USERNAME.toLowerCase()
  ) {
    return DEFAULT_USER_EMAIL;
  }
  return trimmed;
}

export function isDefaultUser(email: string | null | undefined): boolean {
  return (email ?? '').toLowerCase() === DEFAULT_USER_EMAIL;
}

export function isAccountClaimed(): boolean {
  return localStorage.getItem(CLAIMED_KEY) === '1';
}

export function markAccountClaimed(): void {
  localStorage.setItem(CLAIMED_KEY, '1');
}

export function hasSeededDefaultFolders(): boolean {
  return localStorage.getItem(SEEDED_KEY) === '1';
}

export function markDefaultFoldersSeeded(): void {
  localStorage.setItem(SEEDED_KEY, '1');
}

/** Fetch the singleton default_workspace row (readable by anyone). */
export async function fetchDefaultWorkspace(): Promise<DefaultWorkspace | null> {
  // default_workspace is not in the generated Database types yet.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('default_workspace')
    .select('default_user_id, folders, file_folders')
    .eq('id', 1)
    .maybeSingle();

  if (error || !data) return null;
  const row = data as {
    default_user_id: string;
    folders: unknown;
    file_folders: unknown;
  };
  return {
    defaultUserId: row.default_user_id,
    folders: Array.isArray(row.folders) ? (row.folders as Folder[]) : [],
    fileFolders:
      row.file_folders && typeof row.file_folders === 'object'
        ? (row.file_folders as Record<string, string>)
        : {},
  };
}

/**
 * Account claiming / guest-to-user conversion. Run once after a real account is
 * created: clones the starter conversations into the new account (server-side
 * RPC, returns old→new conversation id map) and remaps the locally-seeded folder
 * assignments onto the new conversation ids. Idempotent via the claimed flag.
 */
export async function claimDefaultWorkspace(): Promise<void> {
  if (isAccountClaimed()) return;

  // claim_default_workspace is not in the generated Database types yet.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)(
    'claim_default_workspace',
  );
  if (error) throw error;

  const idMap = (data ?? {}) as Record<string, string>;

  try {
    const raw = localStorage.getItem(ASSIGNMENTS_KEY);
    const assignments = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    const remapped: Record<string, string> = {};
    for (const [convId, folderId] of Object.entries(assignments)) {
      remapped[idMap[convId] ?? convId] = folderId;
    }
    localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(remapped));
  } catch {
    // best-effort remap; ignore malformed localStorage
  }

  markAccountClaimed();
}

/**
 * Publish the current localStorage folder layout as the starter workspace.
 * Only meaningful when signed in as the Default user (RLS enforces it).
 */
export async function publishDefaultWorkspace(): Promise<void> {
  const folders = JSON.parse(localStorage.getItem(FOLDERS_KEY) ?? '[]');
  const fileFolders = JSON.parse(localStorage.getItem(ASSIGNMENTS_KEY) ?? '{}');
  // default_workspace is not in the generated Database types yet.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from('default_workspace')
    .update({
      folders,
      file_folders: fileFolders,
      updated_at: new Date().toISOString(),
    })
    .eq('id', 1);
  if (error) throw error;
}
